import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CustomBackdrop, NavHeader, Page, SubmitButton } from '../../../../../../components';
import { ChannelTypeInternal, MeterTypeInternal } from '../../../../../../data-access/gql-types/graphql';
import { useDevicesAndChannelsContext } from '../../../../context/devices-and-channels';
import { ChannelMeterTypeOrUndefined } from '../../../../types';
import { useGetChannelDetails } from '../../../channel-details/hooks/use-get-channel-details';
import { RouteParams } from '../../../channel-details/types';
import { TariffForm } from '../../../common/tariff';
import { TariffPresentation } from '../../../common/tariff/presentation/presentation';
import { TariffPrefix } from '../../../common/tariff/types';
import { useTariffConfigurationContext } from '../../context/tariff-configuration-context';
import { useMeterTariffConfiguration } from '../../hooks/use-meter-tariff-configuration';
import { useMeterTariffPricesConfiguration } from '../../hooks/use-meter-tariff-prices-configuration';
import { usePartialConfigChanged } from '../../hooks/use-partial-config-changed';
import TariffPrices from '../tariff-prices';

const TariffConfigurationForm: React.FC = () => {
  const { channelId } = useParams<RouteParams>();
  const { t } = useTranslation('configuration');
  const { channel } = useDevicesAndChannelsContext();
  const ChannelId = channelId ?? '';
  const { loading } = useGetChannelDetails(ChannelId);
  const { form, formValues } = useTariffConfigurationContext();
  const { setMeterTariffData, handleSubmitTariffData } = useMeterTariffConfiguration();
  useMeterTariffPricesConfiguration();
  const channelTariff = (channel?.data as ChannelMeterTypeOrUndefined)?.tariff;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    if (channelTariff) setMeterTariffData(channelTariff);
  }, [JSON.stringify(channelTariff)]);

  const isStandalone =
    channel?.data?.type === ChannelTypeInternal.Meter && channel?.data?.meterType === MeterTypeInternal.Standalone;

  const { setTimeoutError } = usePartialConfigChanged({ channelId: ChannelId });

  const onSubmit = (e) => {
    e.preventDefault();

    handleSubmitTariffData(setTimeoutError);
  };

  return (
    <Page containerClassName="page__container--flex-height" header={<NavHeader title={t('tariffsConfiguration')} />}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <TariffForm
            type={ChannelTypeInternal.Meter}
            customTariff={false}
            isDouble={!isStandalone}
            {...(!isStandalone && { availableTariffs: [TariffPrefix.G11, TariffPrefix.Dynamic] })}
            hidePricesInfo
          />
          {formValues.prices.length ? (
            <>
              <TariffPrices />
              <hr className="m-b-24" />
            </>
          ) : null}

          <SubmitButton disabled={!isDirty} />
        </form>
        <TariffPresentation />
      </FormProvider>
      <CustomBackdrop loading={loading} />
    </Page>
  );
};

export default TariffConfigurationForm;
